import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import logo from "../Images/CTF LOGO 1.png";

// navData.js
const navBarData = [
  { label: "Home", url: "/" },
  { label: "About Us", url: "/about" },
  {
    label: "Courses",
    submenu: [
      { label: "All Courses", url: "/all-courses", icon: "fa-book" },
      { label: "CTF Courses", url: "/ctf_courses", icon: "fa-flag" },
      { label: "University", url: "/university", icon: "fa-graduation-cap" },
      { label: "Mauritius", url: "/mauritius", icon: "fa-globe" },
      { label: "Internships", url: "/internships", icon: "fa-briefcase" },
    ],
  },
  {
    label: "Student Corner",
    submenu: [
      { label: "Student Login", url: "/student/login", icon: "fa-user-graduate" },
      { label: "Certificate Verification", url: "/student-certificate", icon: "fa-certificate" },
      { label: "Announcements", url: "/student-announcements", icon: "fa-bullhorn" },
    ],
  },

  { label: "Gallery", url: "/gallery" },
  { label: "Contact", url: "/contact" },
  {
    label: "Login",
    submenu: [
      { label: "Admin", url: "/admin/login", icon: "fa-user-shield" },
      { label: "Member", url: "/member/login", icon: "fa-user-tie" },
    ],
  },
];

const topBarContactLinks = [
  {
    label: "+91 98982 33038",
    href: "tel:+919898233038",
    icon: "fa-solid fa-phone",
  },
  {
    label: "contact@ctfglobal.in",
    href: "mailto:contact@ctfglobal.in",
    icon: "fa-regular fa-envelope",
  },
];

const topBarSocialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/ctfglobal",
    icon: "fab fa-facebook-f",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/ctf_global",
    icon: "fab fa-instagram",
  },
  {
    label: "WhatsApp",
    href: "https://wa.me/919227778181",
    icon: "fab fa-whatsapp",
  },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [navbarTopOffset, setNavbarTopOffset] = useState(0);
  const [navbarHeight, setNavbarHeight] = useState(84);
  const dropdownRef = useRef(null);
  const topbarRef = useRef(null);
  const navbarRef = useRef(null);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  // outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let rafId = 0;
    const updateNavbarLayout = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        const topbarHeight = topbarRef.current?.offsetHeight || 0;
        const currentNavbarHeight = navbarRef.current?.offsetHeight || 84;
        const scrollY = window.scrollY || window.pageYOffset || 0;

        setNavbarHeight(currentNavbarHeight);
        setNavbarTopOffset(Math.max(topbarHeight - scrollY, 0));
      });
    };

    updateNavbarLayout();
    window.addEventListener("scroll", updateNavbarLayout, { passive: true });
    window.addEventListener("resize", updateNavbarLayout);
    window.addEventListener("orientationchange", updateNavbarLayout);

    let resizeObserver;
    if ("ResizeObserver" in window && typeof window.ResizeObserver === "function") {
      resizeObserver = new window.ResizeObserver(updateNavbarLayout);
      if (topbarRef.current) resizeObserver.observe(topbarRef.current);
      if (navbarRef.current) resizeObserver.observe(navbarRef.current);
    }

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", updateNavbarLayout);
      window.removeEventListener("resize", updateNavbarLayout);
      window.removeEventListener("orientationchange", updateNavbarLayout);
      resizeObserver?.disconnect();
    };
  }, []);

  // ======= RENDER MENU FUNCTION =======
  const renderMenu = (items) => {
    return items.map((item, index) => {
      // ===== Dropdown =====
      if (item.submenu) {
        const isLogin = item.label === "Login";
        const menuId = `menu-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
        return (
          <li className="nav-item dropdown" key={index} ref={dropdownRef}>
            <button
              type="button"
              className={`dropdown-toggle ${
                openDropdown === item.label ? "active" : ""
              } ${isLogin ? "login-btn" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdown(item.label);
              }}
              style={
                isLogin
                  ? {
                      backgroundColor: "var(--primary)",
                      color: "#ffffff",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      fontWeight: "500",
                    }
                  : {}
              }
              aria-haspopup="true"
              aria-expanded={openDropdown === item.label}
              aria-controls={menuId}
            >
              {item.label}
              <i
                className={`fa-solid fa-chevron-down ${
                  openDropdown === item.label ? "rotate-180" : ""
                }`}
                style={isLogin ? { color: "#ffffff" } : {}}
              ></i>
            </button>

            <div
              id={menuId}
              className={`dropdown-menu ${
                openDropdown === item.label ? "show" : ""
              }`}
              role="menu"
            >
              {item.submenu.map((sub, i) => (
                <NavLink
                  to={sub.url}
                  key={i}
                  className="dropdown-item"
                  onClick={closeMenu}
                  role="menuitem"
                >
                  {sub.icon && <i className={`fa-solid ${sub.icon}`}></i>}
                  {sub.label}
                </NavLink>
              ))}
            </div>
          </li>
        );
      }

      // ===== Normal Link =====
      return (
        <li className="nav-item" key={index}>
          <NavLink
            to={item.url}
            className={({ isActive }) =>
              `nav-link ${isActive ? "active" : ""}`
            }
            onClick={closeMenu}
          >
            {item.label}
          </NavLink>
        </li>
      );
    });
  };

  return (
    <>
      <div ref={topbarRef} className="navbar-topbar">
        <div className="navbar-topbar-inner">
          <div className="navbar-topbar-left" aria-label="Quick contact">
            {topBarContactLinks.map((item) => (
              <a key={item.label} href={item.href} className="navbar-topbar-link">
                <i className={item.icon} aria-hidden="true"></i>
                <span>{item.label}</span>
              </a>
            ))}
          </div>

          <div className="navbar-topbar-right" aria-label="Social media links">
            {topBarSocialLinks.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="navbar-topbar-social"
                target="_blank"
                rel="noreferrer"
                aria-label={item.label}
                title={item.label}
              >
                <i className={item.icon} aria-hidden="true"></i>
              </a>
            ))}
          </div>
        </div>
      </div>

      <nav
        ref={navbarRef}
        className="navbar"
        aria-label="Primary"
        style={{ top: `${navbarTopOffset}px` }}
      >
        <div className="navbar-container">
          <NavLink to="/" className="navbar-brand" onClick={closeMenu}>
            <img src={logo} alt="CTF Logo" className="logo-img" width="512" height="512" fetchPriority="high" />
            <span className="brand-name" aria-label="Computer Technology Foundation">
              <span className="brand-name-line">Computer</span>
              <span className="brand-name-line">Technology</span>
              <span className="brand-name-line">Foundation</span>
            </span>
          </NavLink>

          {/* ===== Mobile Toggle ===== */}
          <button
            type="button"
            className={`navbar-toggler ${isMenuOpen ? "active" : ""}`}
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-controls="site-navigation"
          >
            <i className="fa fa-bars" aria-hidden="true"></i>
          </button>

          {/* ===== MENU ===== */}
          <div
            id="site-navigation"
            className={`navbar-menu ${isMenuOpen ? "show" : ""}`}
            aria-hidden={!isMenuOpen}
            style={{ top: `${navbarTopOffset + navbarHeight}px` }}
          >
            <ul className="navbar-nav">{renderMenu(navBarData)}</ul>
          </div>
        </div>
      </nav>
      <div className="navbar-spacer" aria-hidden="true" style={{ height: `${navbarHeight}px` }}></div>
    </>
  );
};

export default Navbar;
